import { useMemo, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { Box, ConfirmationModal, RadioButtonWithLabel, Typography } from 'seagull';
import { useDeleteProposal } from "./hooks/useDeleteProposal";
import { useArchiveProposal } from "./hooks/useArchiveProposal";
import { useDraftProposal } from "../../../hooks/reactQuery/mutations/useDraftProposal";
import styles from "./WithdrawModal.module.scss";
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
export const WithdrawModalTestIds = pathgen('WithdrawModalTestIds');
export let WithdrawModalAction = /*#__PURE__*/function (WithdrawModalAction) {
  WithdrawModalAction[WithdrawModalAction["Archive"] = 0] = "Archive";
  WithdrawModalAction[WithdrawModalAction["Delete"] = 1] = "Delete";
  WithdrawModalAction[WithdrawModalAction["Draft"] = 2] = "Draft";
  return WithdrawModalAction;
}({});
export const WithdrawModal = _ref => {
  let {
    relationshipId,
    draftId,
    onSuccess,
    dismissModal,
    openedBy
  } = _ref;
  const [action, setAction] = useState(WithdrawModalAction.Archive);
  const onSuccessAndDismiss = () => {
    onSuccess();
    dismissModal();
  };
  const {
    archive,
    isLoading: isArchiving
  } = useArchiveProposal({
    onSuccess: onSuccessAndDismiss,
    biCode: openedBy === 'agreement-studio' ? 18807 : 18804
  });
  const {
    draftProposal,
    isLoading: isDrafting
  } = useDraftProposal({
    onSuccess: onSuccessAndDismiss,
    withPageLoader: false,
    biCode: openedBy === 'agreement-studio' ? 18808 : 18805
  });
  const {
    deleteProposal,
    isLoading: isDeleting
  } = useDeleteProposal({
    onSuccess: onSuccessAndDismiss,
    biCode: openedBy === 'agreement-studio' ? 18809 : 18806
  });
  const isLoading = isArchiving || isDrafting || isDeleting;
  const primaryButton = useMemo(() => {
    switch (action) {
      case WithdrawModalAction.Archive:
        return {
          label: 'Archive proposal',
          onClick: () => {
            return archive({
              draftId,
              relationshipId
            });
          },
          disabled: false,
          testid: WithdrawModalTestIds.ArchiveConfirmButton()
        };
      case WithdrawModalAction.Draft:
        return {
          label: 'Move proposal to Drafts',
          onClick: () => {
            return draftProposal({
              draftId,
              relationshipId
            });
          },
          disabled: false,
          testid: WithdrawModalTestIds.DraftConfirmButton()
        };
      case WithdrawModalAction.Delete:
        return {
          label: 'Permanently delete proposal',
          onClick: () => {
            return deleteProposal(relationshipId);
          },
          disabled: false,
          testid: WithdrawModalTestIds.DeleteConfirmButton()
        };
    }
  }, [action]);
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    skin: "danger",
    width: "590px",
    title: "Withdraw Proposal",
    "data-testid": WithdrawModalTestIds.Root(),
    primaryButtonTestid: primaryButton.testid,
    primaryButtonLabel: primaryButton.label,
    onPrimaryButtonClick: primaryButton.onClick,
    primaryButtonLoading: isLoading,
    secondaryButtonTestid: WithdrawModalTestIds.SecondaryCloseButton(),
    textAlignCenter: false,
    onCloseButtonClick: dismissModal,
    secondaryButtonLabel: "Cancel",
    onSecondaryButtonClick: dismissModal,
    content: /*#__PURE__*/_jsxs(Box, {
      className: styles.root,
      children: [/*#__PURE__*/_jsx(Box, {
        className: styles.header,
        children: /*#__PURE__*/_jsxs(Typography, {
          children: ["Proposals that have been withdrawn are no longer accessible to your clients.", ' ']
        })
      }), /*#__PURE__*/_jsxs(Box, {
        className: styles.content,
        children: [/*#__PURE__*/_jsx(Typography, {
          children: "What action would you like to take with this proposal?"
        }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
          label: "Archive it",
          testId: WithdrawModalTestIds.RadioOptions.Archive(),
          checked: action === WithdrawModalAction.Archive,
          onChange: () => setAction(WithdrawModalAction.Archive)
        }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
          label: "Move it back to drafts",
          testId: WithdrawModalTestIds.RadioOptions.Draft(),
          checked: action === WithdrawModalAction.Draft,
          onChange: () => setAction(WithdrawModalAction.Draft)
        }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
          label: "Delete it permanently",
          testId: WithdrawModalTestIds.RadioOptions.Delete(),
          checked: action === WithdrawModalAction.Delete,
          onChange: () => setAction(WithdrawModalAction.Delete)
        })]
      })]
    })
  });
};